import React, { forwardRef, useRef, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { GradientColorHeader, ProductInfo, ReadTextField, ScrollableTable } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { Box, Button, TextField, DateTimeStandard } from "@curves/ui-base";
import { Typography } from "@curves/ui-base";
import { getNowTime, parseDate } from "@util/moment";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import useClubPurchaseReceiptApi from "@apis/useClubPurchaseReceiptApi";
import useClubExceptionOrderApi from "@apis/useClubExceptionOrderApi";
import { getProductRowKey } from "@clubInventory/utils";

const PRODUCT_LIST_HEADER = [
	{
		cellKey: "productName",
		headerLabel: "商品清單",
		minWidth: 200,
		formatCell: (productName, row) => (
			<ProductInfo
				product={{
					name: productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
			/>
		),
	},
	{
		cellKey: "shouldInspectionQuantity",
		headerLabel: "未入庫",
		align: "center",
		minWidth: 100,
		formatCell: val => <Typography color="error">{Number(val ?? 0)}</Typography>,
	},
];

const toExceptionItem = row => ({
	productID: row.productID,
	variantID: Number(row.variantID ?? 0),
	quantity: Number(row.shouldInspectionQuantity ?? 0),
});

// 功能：進貨單結單（填寫原因、產生異常單）
const PurchaseOrderCloseDialog = forwardRef(({ open, onClose, sourceData, refresh, ...props }, ref) => {
	const assigneeRef = useRef(null);
	const reasonRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();
	const { getClubPurchaseReceiptApi } = useClubPurchaseReceiptApi();
	const { postClubExceptionOrderApi } = useClubExceptionOrderApi();
	const [receiptData, setReceiptData] = useState(null);

	const clubPurchaseReceiptID = sourceData?.clubPurchaseReceiptID;
	const receiptItems = Array.isArray(receiptData?.items) ? receiptData.items : [];

	const fetchReceipt = useCallback(async () => {
		if (!clubPurchaseReceiptID) return;
		const resp = await getClubPurchaseReceiptApi(clubPurchaseReceiptID);
		if (resp) setReceiptData(resp);
		// eslint-disable-next-line
	}, [clubPurchaseReceiptID, getClubPurchaseReceiptApi]);

	useEffect(() => {
		if (!open || !clubPurchaseReceiptID) {
			setReceiptData(null);
			return;
		}
		fetchReceipt();
		// eslint-disable-next-line
	}, [open, clubPurchaseReceiptID, fetchReceipt]);

	const handleClose = e => isFunction(onClose) && onClose(e);

	const handleSubmit = async () => {
		if (refIsRequiredError([assigneeRef, reasonRef])) return;

		const payload = {
			clubPurchaseReceiptID,
			purchaseDate: getNowTime(),
			employeeID: callRef(assigneeRef, "getResult"),
			exceptionReason: callRef(reasonRef, "getResult"),
			items: receiptItems.map(toExceptionItem),
		};
		const resp = await postClubExceptionOrderApi(payload);

		if (resp != null && !resp.isError) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	const displayDate = receiptData?.purchaseDate ?? getNowTime();
	const dateText = parseDate(displayDate, DateTimeStandard.DisplayDateBySlash);

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-80rem" }} open={open} onClose={handleClose} fullWidth {...props}>
			<GradientColorHeader onClose={handleClose}>確定要結單嗎?</GradientColorHeader>
			<DialogContent className="p-4">
				<Grid container spacing={3}>
					<Grid xs={6} item>
						<ReadTextField label="建單日期">{dateText}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<EmployeeSelect label="擔當" ref={assigneeRef} options={employeeOption} fullWidth required />
					</Grid>
					<Grid xs={12} item>
						<TextField label="原因" ref={reasonRef} rows={2} multiline fullWidth required />
					</Grid>
					<Grid xs={12} item>
						<ScrollableTable headerRow={PRODUCT_LIST_HEADER} rowsData={receiptItems} rowKey={getProductRowKey} tableClassName="theme-gray" />
					</Grid>
				</Grid>
				<Box className="mt-3 text-right">
					<Button variant="contained" onClick={handleSubmit}>
						確認
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
});

PurchaseOrderCloseDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.object,
	refresh: PropTypes.func,
};

PurchaseOrderCloseDialog.displayName = "PurchaseOrderCloseDialog";

export default PurchaseOrderCloseDialog;
